/**
 * **舞台の設定を書く欄**（Issue #643）。タイミング調節のページの「最初の設定」と「設定の変化」の両方で使う。
 *
 * 書くのは**一部の項目だけ**（`editorPorts/timingSettings.js`）。どの項目も「（変えない）」を選べて、
 * そのときはキーごと消す（＝前の値のまま）。層の濃さは層ごとに「変える」を入れたものだけ書く。
 */
import {
  BINARY_LAYERS, LAYER_LABELS, SETTING_DEFAULTS, SETTING_FIELD_LABELS, SETTING_FIELD_LAYERS, SETTING_FIELDS, SETTING_LABELS,
  SETTING_OBJECT_LAYERS, SETTING_SHOT_LABELS, SETTING_SHOTS,
} from '../editorPorts/timingSettings';

const KEEP = '';
const row = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 4 };
const name = { width: 88, flex: 'none' };

/** 選ぶ項目（`（変えない）` ＋ 値） */
function Choice({ label, value, options, onChange, testId }) {
  return (
    <label style={row}>
      <span style={name}>{label}</span>
      <select value={value ?? KEEP} data-testid={testId} onChange={(e) => onChange(e.target.value === KEEP ? null : e.target.value)}>
        <option value={KEEP}>（変えない）</option>
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select>
    </label>
  );
}

/** 出す / 消す の項目 */
function Toggle({ label, value, onChange, testId }) {
  const v = typeof value === 'boolean' ? String(value) : KEEP;
  return (
    <Choice
      label={label} value={v} testId={testId}
      options={[['true', '出す'], ['false', '消す']]}
      onChange={(next) => onChange(next == null ? null : next === 'true')}
    />
  );
}

/** 層ごとの濃さ（層ごとに「変える」を入れたものだけ） */
function Opacities({ label, names, value, defaults, onChange, testId }) {
  const set = (n, v) => {
    const next = { ...(value ?? {}) };
    if (v == null) delete next[n]; else next[n] = v;
    onChange(Object.keys(next).length ? next : null);
  };
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 12, opacity: 0.8 }}>{label}</div>
      {names.map((n) => {
        // **0 / 1 だけの層**（カード）は 出す / 出さない の選択（濃さのスライダーは出さない）
        if (BINARY_LAYERS.includes(n)) {
          const v = Number.isFinite(value?.[n]) ? String(value[n] >= 0.5 ? 1 : 0) : KEEP;
          return (
            <label key={n} style={{ ...row, paddingLeft: 8 }}>
              <span style={{ width: 144, flex: 'none' }}>{LAYER_LABELS[n] ?? n}</span>
              <select value={v} data-testid={`${testId}-${n}`} onChange={(e) => set(n, e.target.value === KEEP ? null : Number(e.target.value))}>
                <option value={KEEP}>（変えない）</option>
                <option value="1">出す</option>
                <option value="0">出さない（motion も呼ばない）</option>
              </select>
            </label>
          );
        }
        const on = Number.isFinite(value?.[n]);
        const v = on ? value[n] : (defaults[n] ?? 0);
        return (
          <label key={n} style={{ ...row, paddingLeft: 8 }}>
            <input type="checkbox" checked={on} data-testid={`${testId}-${n}`} onChange={(e) => set(n, e.target.checked ? v : null)} />
            <span style={{ width: 120, flex: 'none', opacity: on ? 1 : 0.5 }}>{LAYER_LABELS[n] ?? n}</span>
            <input type="range" min={0} max={1} step={0.05} value={v} disabled={!on} onChange={(e) => set(n, Number(e.target.value))} />
            <span style={{ width: 34, textAlign: 'right', opacity: on ? 1 : 0.5 }}>{v.toFixed(2)}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * @param value 設定（一部）
 * @param onChange 新しい設定（一部。変えない項目はキーごと無い）
 * @param defaults 層の濃さの「変える」を入れたときの初めの値（その時刻の値）
 */
export default function SettingsEditor({ value = {}, onChange, defaults = SETTING_DEFAULTS, testId = 'settings' }) {
  const put = (key, v) => {
    const next = { ...value };
    if (v == null) delete next[key]; else next[key] = v;
    onChange(next);
  };
  return (
    <div data-testid={testId}>
      <Choice
        label={SETTING_LABELS.field} value={value.field} testId={`${testId}-field`}
        options={SETTING_FIELDS.map((f) => [f, SETTING_FIELD_LABELS[f]])} onChange={(v) => put('field', v)}
      />
      <Choice
        label={SETTING_LABELS.shot} value={value.shot} testId={`${testId}-shot`}
        options={SETTING_SHOTS.map((s) => [s, SETTING_SHOT_LABELS[s] ?? s])} onChange={(v) => put('shot', v)}
      />
      <Toggle label={SETTING_LABELS.hud} value={value.hud} testId={`${testId}-hud`} onChange={(v) => put('hud', v)} />
      <Toggle label={SETTING_LABELS.zoneLabels} value={value.zoneLabels} testId={`${testId}-zoneLabels`} onChange={(v) => put('zoneLabels', v)} />
      <Opacities
        label={SETTING_LABELS.fieldLayers} names={SETTING_FIELD_LAYERS} value={value.fieldLayers}
        defaults={defaults.fieldLayers ?? {}} testId={`${testId}-fieldLayers`} onChange={(v) => put('fieldLayers', v)}
      />
      <Opacities
        label={SETTING_LABELS.objectLayers} names={SETTING_OBJECT_LAYERS} value={value.objectLayers}
        defaults={defaults.objectLayers ?? {}} testId={`${testId}-objectLayers`} onChange={(v) => put('objectLayers', v)}
      />
    </div>
  );
}
